// resendHelper.js - Complete Resend Invitation Helper
import React, { useState } from 'react';
import { doc, updateDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import './resendHelper.css';

// ✅ HELPER FUNCTION: Create Resend Email Job
export const createResendEmailJob = async (teamId, invitation, newEmail, teamInfo, currentUser) => {
  try {
    console.log('📧 Creating resend email job...');
    
    const emailJobData = {
      teamId: teamId,
      invites: [{
        email: newEmail.trim(),
        code: invitation.code // ✅ BEHOUD ORIGINELE CODE!
      }],
      teamInfo: {
        teamName: teamInfo.name,
        createdBy: teamInfo.createdBy,
        organizationId: teamInfo.organizationId
      },
      status: 'pending',
      createdAt: serverTimestamp(),
      createdBy: currentUser.email,
      isResend: true // ✅ Markeer als resend voor tracking
    };

    // Create email job document (triggers Firebase Function automatically)
    const emailJobRef = await addDoc(collection(db, 'email_jobs'), emailJobData);
    console.log('✅ Resend email job created:', emailJobRef.id);
    
    return { success: true, jobId: emailJobRef.id };
    
  } catch (error) {
    console.error('❌ Create resend email job error:', error);
    return { success: false, error: error.message };
  }
};

// ✅ HELPER FUNCTION: Update Invitation Email
export const updateInvitationEmail = async (teamId, invitationId, newEmail) => {
  try {
    console.log('🔄 Updating invitation email...');
    
    const invitationRef = doc(db, 'teams', teamId, 'invites', invitationId);
    await updateDoc(invitationRef, {
      email: newEmail.trim(),
      lastResent: serverTimestamp()
    });
    
    console.log('✅ Invitation email updated');
    return { success: true };
    
  } catch (error) {
    console.error('❌ Update invitation error:', error);
    return { success: false, error: error.message };
  }
};

// ✅ HELPER FUNCTION: Validate Resend Data
export const validateResendData = (selectedInvitation, newEmail, invitations) => {
  if (!selectedInvitation) {
    return { isValid: false, message: 'Selecteer een uitnodiging' };
  }
  
  if (!newEmail || !newEmail.trim()) {
    return { isValid: false, message: 'Voer een emailadres in' };
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(newEmail.trim())) {
    return { isValid: false, message: 'Voer een geldig emailadres in' };
  }
  
  const invitation = invitations.find(inv => inv.id === selectedInvitation);
  if (!invitation) {
    return { isValid: false, message: 'Uitnodiging niet gevonden' };
  }
  
  return { isValid: true, invitation };
};

// ✅ HELPER FUNCTION: Smart Email Suggestion
export const suggestCorrectedEmail = (originalEmail) => {
  let suggestedEmail = originalEmail;
  
  // Common email typos
  if (originalEmail.includes('@gmai.')) {
    suggestedEmail = originalEmail.replace('@gmai.', '@gmail.');
  } else if (originalEmail.includes('@hotmai.')) {
    suggestedEmail = originalEmail.replace('@hotmai.', '@hotmail.');
  } else if (originalEmail.includes('@outlok.')) {
    suggestedEmail = originalEmail.replace('@outlok.', '@outlook.');
  } else if (originalEmail.includes('@yahooo.')) {
    suggestedEmail = originalEmail.replace('@yahooo.', '@yahoo.');
  }
  
  return suggestedEmail;
};

// ✅ CUSTOM HOOK: useResendModal
export const useResendModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const openModal = () => {
    console.log('🔧 ResendHelper: Opening modal');
    setIsOpen(true);
    setSelectedInvitation('');
    setNewEmail('');
    setError(null);
    setSuccess(false);
  };

  const closeModal = () => {
    console.log('🔧 ResendHelper: Closing modal');
    setIsOpen(false);
    setSelectedInvitation('');
    setNewEmail('');
    setError(null);
    setSuccess(false);
  };

  const selectInvitation = (invitationId, invitations) => {
    setSelectedInvitation(invitationId);
    
    // Auto-suggest corrected email
    const invitation = invitations.find(inv => inv.id === invitationId);
    if (invitation) {
      const suggestedEmail = suggestCorrectedEmail(invitation.email);
      setNewEmail(suggestedEmail);
    }
  };

  const processResend = async (teamId, invitations, teamInfo, currentUser) => {
    setIsProcessing(true);
    setError(null);

    try {
      // Validate input data
      const validation = validateResendData(selectedInvitation, newEmail, invitations);
      if (!validation.isValid) {
        throw new Error(validation.message);
      }

      const invitation = validation.invitation;

      // Update invitation email in database
      const updateResult = await updateInvitationEmail(teamId, selectedInvitation, newEmail);
      if (!updateResult.success) {
        throw new Error(updateResult.error);
      }

      // Create resend email job
      const emailResult = await createResendEmailJob(teamId, invitation, newEmail, teamInfo, currentUser);
      if (!emailResult.success) {
        throw new Error(emailResult.error);
      }

      setSuccess(true);
      console.log('✅ Resend process completed successfully');
      
      // Auto-close modal after success
      setTimeout(() => {
        closeModal();
      }, 2000);

      return { 
        success: true, 
        updatedInvitation: { 
          ...invitation, 
          email: newEmail.trim() 
        }
      };

    } catch (error) {
      console.error('❌ Resend process error:', error);
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    isOpen,
    selectedInvitation,
    newEmail,
    isProcessing,
    error,
    success,
    openModal,
    closeModal,
    selectInvitation,
    processResend,
    setNewEmail
  };
};

// ✅ REACT COMPONENT: ResendModal
export const ResendModal = ({ 
  isOpen, 
  invitations, 
  responses, 
  teamId, 
  teamInfo, 
  currentUser,
  selectedInvitation,
  newEmail,
  isProcessing,
  error,
  success,
  onClose,
  onSelectInvitation,
  onEmailChange,
  onProcessResend
}) => {
  
  if (!isOpen) {
    return null;
  }

  const handleSubmit = async () => {
    const result = await onProcessResend(teamId, invitations, teamInfo, currentUser);
    // Result handling is done in the hook
  };

  return (
    <div className="resend-overlay">
      <div className="resend-modal">
        <div className="resend-header">
          <h3 className="resend-title">📧 Uitnodiging opnieuw versturen</h3>
          <button 
            className="resend-close"
            onClick={onClose}
            type="button"
          >
            ✕
          </button>
        </div>
        
        <div className="resend-body">
          
          {success ? (
            // Success State
            <div className="resend-success-state">
              <div className="resend-success-icon">✅</div>
              <h4 className="resend-success-title">Uitnodiging Verzonden!</h4>
              <p className="resend-success-text">
                De uitnodiging is opnieuw verzonden naar het nieuwe emailadres.
              </p>
            </div>
          ) : (
            // Form State
            <>
              <div className="resend-form-group">
                <label className="resend-label">
                  Origineel verzonden naar:
                </label>
                <select 
                  className="resend-select" 
                  value={selectedInvitation}
                  onChange={(e) => onSelectInvitation(e.target.value, invitations)}
                  disabled={isProcessing}
                >
                  <option value="">Selecteer emailadres...</option>
                  {invitations.map((invitation) => {
                    const isCompleted = responses.some(r => r.invitationId === invitation.id);
                    return (
                      <option key={invitation.id} value={invitation.id}>
                        {invitation.email} {isCompleted ? '(✅ Quiz ingevuld)' : '(📧 Verzonden)'}
                      </option>
                    );
                  })}
                </select>
                <div className="resend-help">
                  Kies het emailadres waarmee de uitnodiging oorspronkelijk is verzonden.
                </div>
              </div>
              
              <div className="resend-form-group">
                <label className="resend-label">
                  Nieuw emailadres:
                </label>
                <input 
                  type="email" 
                  className="resend-input" 
                  value={newEmail}
                  onChange={(e) => onEmailChange(e.target.value)}
                  placeholder="Voer het correcte emailadres in..."
                  disabled={isProcessing}
                />
                <div className="resend-help">
                  De uitnodiging wordt verzonden naar dit nieuwe emailadres met dezelfde unieke code.
                </div>
              </div>

              {error && (
                <div className="resend-error">
                  ❌ {error}
                </div>
              )}
              
              <div className="resend-actions">
                <button 
                  className="resend-btn resend-btn--cancel" 
                  onClick={onClose}
                  type="button"
                  disabled={isProcessing}
                >
                  Annuleren
                </button>
                <button 
                  className="resend-btn resend-btn--submit" 
                  onClick={handleSubmit}
                  disabled={!selectedInvitation || !newEmail || isProcessing}
                  type="button"
                >
                  {isProcessing ? 'Versturen...' : '📧 Verstuur opnieuw'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ✅ DEFAULT EXPORT: Complete Resend System
export default {
  useResendModal,
  ResendModal,
  createResendEmailJob,
  updateInvitationEmail,
  validateResendData,
  suggestCorrectedEmail
};